import React from 'react';
import { StandardTableActions } from '../../../components/ui';

import { Column } from '../../../components/DataTable/DataTable';
import { TaskType } from './useData';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';

export const moduleConfig = {
  module: {
    title: 'Task Types',
    description: 'Manage task types',
    permissionModuleKey: 'task_type',
    route: {
      list: '/task_type',
      new: '/task_type/new',
      detail: (id: number) => `/task_type/${id}`,
      edit: (id: number) => `/task_type/${id}/edit`,
    },
    api: {
      list: '/p-crm-task-type',
      detail: (id: number) => `/p-crm-task-type/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search task types...',
  },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['tt_descr'],
    },
  ],
  filters: {
    fields: [
      {
        key: 'tt_descr',
        label: 'Description',
        placeholder: 'Type to filter description...',
        type: 'text' as const,
        getValue: (item: TaskType) => item.tt_descr,
        options: [],
      },
    ],
    applyFilter: (items: TaskType[], filters: { tt_descr: string[] | string }) => {
      return items.filter((item) => {
        const descrFilter = filters.tt_descr;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.tt_descr || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.tt_descr || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Task Type',
        showTitle: false,
        variant: 'primary' as const,
        href: '/task_type/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: TaskType) => {
      window.location.href = `/task_type/${item.id}`;
    },
    keyExtractor: (item: TaskType) => item.id,
  },
  emptyStates: {
    noData: 'Get started by creating a new task type.',
    noSearchResults: 'No task types found matching your search criteria.',
    noFilterResults: 'No task types match the selected filters.',
  },
  delete: {
    title: 'Delete Task Type',
    message: 'Are you sure you want to delete this task type? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete task type. Please try again.',
    handler: async (id: number) => {
      const { deleteTaskType } = await import('../../../services/mutations');
      await deleteTaskType(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },
  detail: {
    subtitle: 'Task Type Details',
    notFoundTitle: 'Task Type not found',
    notFoundMessage: 'The task type you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Task Types',
    defaultTitle: 'Unnamed Task Type',
    getTitle: (item: TaskType) => {
      return item.tt_descr || `Task Type #${item.id}`;
    },
  },
  form: {
    new: {
      title: 'New Task Type',
      subtitle: 'Add a new task type',
      submitText: 'Create Task Type',
    },
    edit: {
      title: 'Edit Task Type',
      subtitle: 'Update task type information',
      submitText: 'Update Task Type',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading task type...',
    errorMessage: 'Failed to save task type. Please try again.',
    create: async (data: Partial<TaskType>) => {
      const { createTaskType } = await import('../../../services/mutations');
      return await createTaskType(data);
    },
    update: async (id: number, data: Partial<TaskType>) => {
      const { updateTaskType } = await import('../../../services/mutations');
      return await updateTaskType(id, data);
    },
  },
  fields: [
    {
      name: 'tt_descr',
      label: 'Description',
      type: 'text',
      required: true,
      placeholder: 'Enter task type description',
      validation: {
        required: true,
        minLength: 1,
        maxLength: 255,
      },
    },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Task Type Information',
        icon: '📝',
        fields: ['tt_descr'],
        columns: 1,
        collapsible: false,
      },
    ],
  } as FormLayout,
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<TaskType>[] => [
  {
    key: 'id',
    header: 'ID',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
  {
    key: 'tt_descr',
    header: 'Description',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
];

export const getDetailSections = (
  item: TaskType,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📝',
      fields: [
        {
          label: 'Description',
          value: item.tt_descr || '-',
        },
      ],
    },
  ];
};

export const getStatusBadges = (item: TaskType) => {
  return null;
};

export const getInitialFormData = (): Partial<TaskType> => {
  return {
    tt_descr: '',
  };
};

export const mapItemToFormData = (item: TaskType): Partial<TaskType> => {
  return {
    tt_descr: item.tt_descr || '',
  };
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

export const getTableActions = (
  item: TaskType,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

